// components/FilterTopSection.tsx
import {
  Box,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import DoubleArrowLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import DoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import useStyles from "../../workDiary/DiaryComponents/dairyStyle";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getAllSignOffTasks } from "../../../../store/reducers/signOffTaskSlice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface FilterTopSectionProps {
  filteredData: {
    startDate: string;
    endDate: string;
  };
  setFilteredData: React.Dispatch<
    React.SetStateAction<{
      startDate: string;
      endDate: string;
    }>
  >;
}

const FilterTopSection: React.FC<FilterTopSectionProps> = ({
  filteredData,
  setFilteredData,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { signOffTasks } = useSelector((state: any) => state?.signOffTask);
  const { userProfile } = useSelector((state: any) => state?.user);
  
  useEffect(() => {
    dispatch(getAllSignOffTasks({ ...filteredData }));
  }, [dispatch, filteredData]);

  const handleRangeShift = (
    unit: "week" | "month",
    direction: "backward" | "forward",
  ) => {
    const multiplier = direction === "backward" ? -1 : 1;

    const newStart = dayjs(filteredData.startDate).add(multiplier, unit);
    const newEnd = dayjs(filteredData.endDate).add(multiplier, unit);

    setFilteredData({
      ...filteredData,
      startDate: newStart.format("YYYY-MM-DD"),
      endDate: newEnd.format("YYYY-MM-DD"),
    });
  };

  const defaultFilter = {
    search: "",
    startDate: dayjs().startOf("week").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("week").format("YYYY-MM-DD"),
    taskType: "",
  };

  // Date change handlers
  const handleStartDateChange = (newValue: any) => {
    if (!newValue) return;
    
    const newStartDate = newValue.format("YYYY-MM-DD");
    
    setFilteredData((prev) => {
      let updated = { ...prev, startDate: newStartDate };

      // If startDate > endDate → auto update endDate
      if (dayjs(newStartDate).isAfter(dayjs(prev.endDate))) {
        updated.endDate = newStartDate;
      }

      return updated;
    });
  };

  const handleEndDateChange = (newValue: any) => {
    if (!newValue) return;
    
    setFilteredData({
      ...filteredData,
      endDate: newValue.format("YYYY-MM-DD"),
    });
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={"center"}
      mb={2}
    >
      <Typography
        variant="subtitle2"
        fontSize={16}
        color="#009688"
        fontWeight={600}
        align="left"
        width={160}
      >
        Wages/Payment
      </Typography>

      <IconButton 
        size="small" 
        className={classes.iconBtnSmall}
        onClick={() => handleRangeShift("month", "backward")}
      >
        <DoubleArrowLeft />
      </IconButton>

      <IconButton 
        size="small" 
        className={classes.iconBtnSmall}
        onClick={() => handleRangeShift("week", "backward")}
      >
        <ChevronLeft />
      </IconButton>

      {/* Start Date - MUI X DatePicker */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Start Date"
          value={filteredData.startDate ? dayjs(filteredData.startDate) : null}
          onChange={handleStartDateChange}
          format={userProfile?.dateFormat || "DD/MM/YYYY"}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: 140,
                "& .MuiInputBase-root": {
                  fontSize: "0.8rem",
                  height: 30,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                },
                "& .MuiFormLabel-root": {
                  fontSize: "0.8rem",
                },
                marginRight: 1,
              },
            },
          }}
        />
      </LocalizationProvider>

      {/* End Date - MUI X DatePicker */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="End Date"
          value={filteredData.endDate ? dayjs(filteredData.endDate) : null}
          onChange={handleEndDateChange}
          format={userProfile?.dateFormat || "DD/MM/YYYY"}
          minDate={filteredData.startDate ? dayjs(filteredData.startDate) : undefined}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: 140,
                "& .MuiInputBase-root": {
                  fontSize: "0.8rem",
                  height: 30,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                },
                "& .MuiFormLabel-root": {
                  fontSize: "0.8rem",
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      <IconButton 
        size="small" 
        className={classes.iconBtnSmall}
        onClick={() => handleRangeShift("week", "forward")}
      >
        <ChevronRight />
      </IconButton>
      
      <IconButton 
        size="small" 
        className={classes.iconBtnSmall}
        onClick={() => handleRangeShift("month", "forward")}
      >
        <DoubleArrowRight />
      </IconButton>
      
      <Button
        variant="outlined"
        size="small"
        sx={{ ml: 1, textTransform: "none", height: 30 }}
        onClick={() => setFilteredData(defaultFilter)}
      >
        Reset Filters
      </Button>
    </Box>
  );
};

export default FilterTopSection;